/**
 * Razorpay Checkout integration for the online menu.
 *
 * Talks to the gottea-api backend which:
 *   - POST /payments/order   → creates a Razorpay order + a Firestore order doc
 *   - POST /payments/verify  → verifies the signature server-side
 *
 * The backend returns the public Razorpay key id + the razorpay order id so the
 * client never needs to ship the key in the bundle (though the key id is safe
 * to expose).
 */

import { API_BASE_URL, RAZORPAY_CONFIG } from "../config/payments";

const RAZORPAY_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

let scriptLoadPromise = null;

/** Inject the Razorpay checkout.js script exactly once. */
export function loadRazorpayScript() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Razorpay requires a browser environment"));
  }

  if (window.Razorpay) {
    return Promise.resolve();
  }

  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_SRC}"]`);
    if (existing) {
      if (window.Razorpay) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Failed to load Razorpay script")));
      return;
    }

    const script = document.createElement("script");
    script.src = RAZORPAY_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error("Failed to load Razorpay script"));
    };
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

/**
 * Create a Razorpay order via the backend.
 *
 * @param {{ orderRef: string, cafeCode: string, branchName: string, amountPaise: number, customer: object, items: array, totals: object, notes?: object }} payload
 * @returns {Promise<{ orderId: string, razorpayOrderId: string, amount: number, currency: string, keyId: string }>}
 */
export async function createOrderOnBackend(payload) {
  const response = await fetch(`${API_BASE_URL}/payments/order`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok || !data?.success || !data?.data?.razorpayOrderId) {
    const error = new Error(data?.error || `Order creation failed (${response.status})`);
    error.status = response.status;
    error.code = data?.code || data?.data?.code || "";
    error.data = data?.data || null;
    throw error;
  }

  return data.data;
}

/**
 * Preview Got Tokens using the backend's authoritative loyalty rules.
 * This endpoint is public and read-only so it can be shown before OTP login.
 */
export async function getOrderTokenEstimate(items, { signal } = {}) {
  const response = await fetch(`${API_BASE_URL}/payments/token-estimate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
    signal,
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok || !data?.success || !Number.isFinite(Number(data?.data?.totalTokens))) {
    throw new Error(data?.error || `Got Token estimate failed (${response.status})`);
  }

  return {
    totalTokens: Math.max(0, Number(data.data.totalTokens)),
    tokenItems: Array.isArray(data.data.tokenItems) ? data.data.tokenItems : [],
  };
}

/**
 * Verify a payment server-side.
 *
 * @param {{ orderId: string, razorpayOrderId: string, razorpayPaymentId: string, razorpaySignature: string }} payload
 * @returns {Promise<{ verified: boolean, status: string }>}
 */
export async function verifyPaymentOnBackend(payload) {
  const response = await fetch(`${API_BASE_URL}/payments/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok || !data?.success) {
    throw new Error(data?.error || `Payment verification failed (${response.status})`);
  }

  return { verified: Boolean(data.data?.verified), status: data.data?.status };
}

/**
 * Fetch the backend menu order for receipt/status reconciliation.
 *
 * @param {string} orderId
 * @returns {Promise<object|null>}
 */
export async function getOrderOnBackend(orderId) {
  if (!orderId) return null;

  const response = await fetch(`${API_BASE_URL}/payments/order/${encodeURIComponent(orderId)}`);
  const data = await response.json().catch(() => ({}));

  if (!response.ok || !data?.success) {
    throw new Error(data?.error || `Order fetch failed (${response.status})`);
  }

  return data.data?.order || null;
}

/**
 * Open the Razorpay checkout modal.
 *
 * @param {Object} options
 * @param {number} options.amountPaise  - Amount in paise (₹1 = 100).
 * @param {string} options.keyId        - Public Razorpay key id (from backend).
 * @param {string} options.orderId      - Razorpay order id (from backend).
 * @param {string} [options.customerName]
 * @param {string} [options.customerPhone]
 * @param {string} [options.orderRef]
 * @param {Object} [options.notes]
 * @returns {Promise<{ razorpay_payment_id: string, razorpay_order_id: string, razorpay_signature: string }>}
 */
export function openRazorpayCheckout({
  amountPaise,
  keyId,
  orderId,
  customerName = "",
  customerPhone = "",
  orderRef = "",
  notes = {},
}) {
  return new Promise((resolve, reject) => {
    if (!window.Razorpay) {
      reject(new Error("Razorpay SDK not available"));
      return;
    }

    const options = {
      key: keyId,
      amount: amountPaise,
      currency: "INR",
      name: RAZORPAY_CONFIG.brandName,
      theme: { color: RAZORPAY_CONFIG.brandColor },
      image : 'https://menu.gottea.in/GotTeaLogo.png',
      order_id: orderId,
      notes: { order_ref: orderRef, ...notes },
      prefill: {
        name: customerName,
        contact: customerPhone,
      },
      handler: (response) => resolve(response),
      modal: {
        ondismiss: () => reject(new Error("RAZORPAY_MODAL_DISMISSED")),
      },
    };

    try {
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        reject(new Error(response?.error?.description || "Razorpay payment failed"));
      });
      rzp.open();
    } catch (error) {
      reject(error);
    }
  });
}
